// apps/frontend/src/hooks/profiles/useProfiles.ts
// 👨‍👩‍👧‍👦 HOOK PROFILES ULTRA PREMIUM - VERSION FINALE PARFAITE ✅
// 🎯 CORRIGÉ : Plus de boucle infinie + Cache intelligent + Auto-refresh

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../useAuth';
import { API_BASE_URL } from '../../config/api';

export type ProfileType = 'PARENT' | 'CHILD' | 'BUYER' | 'SELLER' | 'BUSINESS';
export type Gender = 'MALE' | 'FEMALE' | 'UNISEX';

// ============================================================================
// 🎯 TYPES MATCHING EXACTEMENT VOTRE BACKEND API
// ============================================================================

export interface FamilyProfile {
  id: string;
  user_id: string;
  profile_name: string;
  profile_type: ProfileType;
  parent_profile_id?: string;

  personal_info: {
    age?: number;
    gender?: Gender;
    avatar_url?: string;
    birth_date?: string;
  };

  preferences: {
    size_preferences: {
      clothing_sizes: string[];
      shoe_sizes: string[];
      size_tolerance: number;
      auto_growth_tracking: boolean;
    };
    style_preferences: {
      favorite_colors: string[];
      disliked_colors: string[];
      favorite_styles: string[];
      favorite_brands: string[];
      avoided_brands: string[];
    };
    budget_limits: {
      max_price_tops: number;
      max_price_bottoms: number;
      max_price_shoes: number;
      max_price_accessories: number;
      currency: string;
    };
    quality_requirements: {
      min_condition: string;
      branded_only: boolean;
      recent_only: boolean;
    };
    location_preferences: {
      max_distance_km: number;
      accept_shipping: boolean;
      local_pickup_only: boolean;
    };
  };

  permissions: {
    can_purchase: boolean;
    can_sell: boolean;
    requires_approval: boolean;
    max_spend_limit: number;
    restricted_categories: string[];
  };

  stats: {
    total_purchases: number;
    total_spent: number;
    favorite_categories: string[];
    last_active_at: string;
  };

  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface ProfilesState {
  profiles: FamilyProfile[];
  activeProfileId: string | null;
  selectedProfileIds: string[]; // multi-sélection pour filtrer les recommandations
  isLoading: boolean;
  error: string | null;
  hasUnsavedChanges: boolean;
  lastRefreshTime: number;
  needsOnboarding: boolean;
}

// ============================================================================
// 🌐 API SERVICE ULTRA PREMIUM - AVEC CACHE INTELLIGENT
// ============================================================================

class ProfilesAPI {
  private baseUrl = `${API_BASE_URL}/api/v1/users`;
  private getToken: () => string | null;
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5000; // 5 secondes

  constructor(getToken: () => string | null) {
    this.getToken = getToken;
  }

  private getCacheKey(endpoint: string, options?: RequestInit): string {
    return `${endpoint}_${JSON.stringify(options?.body || {})}`;
  }

  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_TTL;
  }

  async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    if (!token) throw new Error('Token non disponible');

    // ✅ CHECK CACHE (sauf pour POST/PUT/DELETE)
    const cacheKey = this.getCacheKey(endpoint, options);
    if (options.method === 'GET' || !options.method) {
      const cached = this.cache.get(cacheKey);
      if (cached && this.isCacheValid(cached.timestamp)) {
        return cached.data;
      }
    }

    // ✅ Anti-cache URL
    const url = new URL(`${this.baseUrl}${endpoint}`);
    url.searchParams.set('_ts', Date.now().toString());

    const response = await fetch(url.toString(), {
      ...options,
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        ...options.headers,
      },
    });

    // ✅ Gère 304
    if (response.status === 304) {
      return {
        success: true,
        data: { profiles: [], pagination: {} },
      } as unknown as T;
    }

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: 'Network error' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();

    // ✅ STORE CACHE (uniquement GET)
    if (options.method === 'GET' || !options.method) {
      this.cache.set(cacheKey, { data, timestamp: Date.now() });
    }

    // ✅ INVALIDATE CACHE après POST/PUT/DELETE
    if (['POST', 'PUT', 'DELETE'].includes(options.method || '')) {
      this.cache.clear();
    }

    return data;
  }

  // ✅ CLEAR CACHE MANUEL
  clearCache(): void {
    this.cache.clear();
  }

  // GET /api/v1/users/profiles
  async getFamilyProfiles(
    params: {
      page?: number;
      limit?: number;
      profile_type?: ProfileType;
    } = {}
  ): Promise<{ profiles: FamilyProfile[]; pagination: any }> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.profile_type) query.set('profile_type', params.profile_type);

    const response = await this.makeRequest<{
      success: boolean;
      data: { profiles: FamilyProfile[]; pagination: any };
    }>(`/profiles${query.toString() ? `?${query.toString()}` : ''}`);

    return response.data;
  }

  // POST /api/v1/users/profiles
  async createFamilyProfile(profileData: {
    profile_name: string;
    profile_type: ProfileType;
    parent_profile_id?: string;
    age?: number;
    birth_date?: string;
    gender?: string;
    avatar_url?: string | null;
    personal_info?: any;
    preferences?: any;
    permissions?: any;
  }): Promise<FamilyProfile> {
    const response = await this.makeRequest<{
      success: boolean;
      data: { profile: FamilyProfile };
    }>('/profiles', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });

    return response.data.profile;
  }

  // DELETE /api/v1/users/profiles/:id
  async deleteFamilyProfile(profileId: string): Promise<void> {
    await this.makeRequest<{
      success: boolean;
      message: string;
    }>(`/profiles/${profileId}`, {
      method: 'DELETE',
    });
  }

  // PUT /api/v1/users/profiles/:id
  async updateFamilyProfile(
    profileId: string,
    updateData: any
  ): Promise<FamilyProfile> {
    const response = await this.makeRequest<{
      success: boolean;
      data: { profile: FamilyProfile };
    }>(`/profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });

    return response.data.profile;
  }
}

// ============================================================================
// 🚀 HOOK useProfiles ULTRA PREMIUM - VERSION FINALE SANS BOUCLE
// ============================================================================

export function useProfiles() {
  const { t } = useTranslation(['common', 'profiles-children']);
  const { user, isAuthenticated } = useAuth();

  const getToken = useCallback(() => {
    const token = localStorage.getItem('glowp_access_token');
    return token;
  }, []);

  const api = useMemo(() => new ProfilesAPI(getToken), [getToken]);

  const [state, setState] = useState<ProfilesState>({
    profiles: [],
    activeProfileId: null,
    selectedProfileIds: JSON.parse(
      localStorage.getItem('glowp_selected_profiles') || '[]'
    ),
    isLoading: false,
    error: null,
    hasUnsavedChanges: false,
    lastRefreshTime: 0,
    needsOnboarding: false,
  });

  // ✅ REF pour éviter boucle infinie
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);

  // ============================================================================
  // 🎯 GETTERS INTELLIGENTS
  // ============================================================================

  const activeProfile = useMemo(() => {
    return state.profiles.find(p => p.id === state.activeProfileId) || null;
  }, [state.profiles, state.activeProfileId]);

  const childProfiles = useMemo(() => {
    return state.profiles.filter(p => p.profile_type === 'CHILD');
  }, [state.profiles]);

  const parentProfile = useMemo(() => {
    return state.profiles.find(p => p.profile_type === 'PARENT') || null;
  }, [state.profiles]);

  const selectedProfiles = useMemo(() => {
    return state.profiles.filter(p => state.selectedProfileIds.includes(p.id));
  }, [state.profiles, state.selectedProfileIds]);

  // ============================================================================
  // 🛠️ ACTIONS CRUD ULTRA PREMIUM - CORRECTION BOUCLE INFINIE
  // ============================================================================

  // ✅ LOAD PROFILES - SANS DÉPENDANCES QUI CHANGENT
  const loadProfiles = useCallback(
    async (
      params: {
        profile_type?: ProfileType;
        page?: number;
        limit?: number;
        forceRefresh?: boolean;
      } = {}
    ) => {
      if (!isAuthenticated || !user || !getToken()) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // ✅ PROTECTION CONTRE APPELS MULTIPLES
      if (isLoadingRef.current && !params.forceRefresh) {
        return;
      }

      isLoadingRef.current = true;

      // ✅ CHECK DANS setState pour éviter dépendance
      setState(prev => {
        const now = Date.now();
        if (
          !params.forceRefresh &&
          prev.profiles.length > 0 &&
          now - prev.lastRefreshTime < 3000
        ) {
          isLoadingRef.current = false;
          return prev;
        }

        return { ...prev, isLoading: true, error: null };
      });

      try {
        if (params.forceRefresh) {
          api.clearCache();
        }

        const data = await api.getFamilyProfiles(params);

        // ✅ AUCUN PROFIL ou PROFIL sans préférences → Onboarding requis
        // La structure API est plate : birth_date et size_preferences sont directement sur le profil
        const parentProfile = data.profiles.find(
          (p: any) => p.profile_type === 'PARENT'
        ) as any;
        const hasNoProfiles = data.profiles.length === 0;
        const hasEmptyPreferences =
          parentProfile &&
          !parentProfile.birth_date &&
          (!parentProfile.size_preferences ||
            Object.keys(parentProfile.size_preferences).length === 0);

        if (hasNoProfiles || hasEmptyPreferences) {
          // Si l'utilisateur a besoin de l'onboarding, on efface tout état "dismissed" persistant
          // (peut rester bloqué à MAX_SAFE_INTEGER suite à des tests du flow vente)
          localStorage.removeItem('glowp_onboarding_dismissed_until');
          setState(prev => ({
            ...prev,
            profiles: data.profiles,
            activeProfileId:
              (
                data.profiles.find((p: any) => p.profile_type === 'PARENT') ||
                data.profiles[0]
              )?.id || null,
            isLoading: false,
            needsOnboarding: true,
            lastRefreshTime: Date.now(),
          }));
          hasLoadedRef.current = true;
          isLoadingRef.current = false;
          return;
        }

        setState(prev => ({
          ...prev,
          profiles: data.profiles,
          activeProfileId:
            prev.activeProfileId ||
            (
              data.profiles.find((p: any) => p.profile_type === 'PARENT') ||
              data.profiles[0]
            )?.id ||
            null,
          isLoading: false,
          lastRefreshTime: Date.now(),
        }));

        hasLoadedRef.current = true;
        isLoadingRef.current = false;
      } catch (error) {
        console.error('❌ Erreur chargement profils:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : t('profiles-children:errors.loadError', 'Erreur de chargement'),
        }));
        isLoadingRef.current = false;
      }
    },
    [api, t, user, isAuthenticated, getToken]
  );

  // ✅ CREATE PROFILE AVEC AUTO-REFRESH
  const createProfile = useCallback(
    async (profileData: {
      profile_name: string;
      profile_type: ProfileType;
      parent_profile_id?: string;
      age?: number;
      birth_date?: string;
      gender?: string;
      avatar_url?: string | null;
      size_preferences?: any;
      color_preferences?: any;
      brand_preferences?: any;
      style_preferences?: any;
      budget_limits?: any;
      personal_info?: any;
      preferences?: any;
      permissions?: any;
    }): Promise<string> => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const newProfile = await api.createFamilyProfile(profileData);

        // ✅ REFRESH AUTO APRÈS CRÉATION
        await loadProfiles({ forceRefresh: true });

        setState(prev => ({
          ...prev,
          activeProfileId: newProfile.id,
          isLoading: false,
          // Don't clear needsOnboarding here — the onboarding wizard manages multi-child flow
        }));

        return newProfile.id;
      } catch (error) {
        console.error('❌ Erreur création:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : t('profiles-children:errors.createError', 'Erreur création'),
        }));
        throw error;
      }
    },
    [api, t, loadProfiles]
  );

  // ✅ DELETE PROFILE AVEC AUTO-REFRESH
  const deleteProfile = useCallback(
    async (profileId: string): Promise<void> => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        await api.deleteFamilyProfile(profileId);

        // ✅ REFRESH AUTO APRÈS SUPPRESSION
        await loadProfiles({ forceRefresh: true });

        setState(prev => {
          let newActiveProfileId = prev.activeProfileId;
          if (prev.activeProfileId === profileId) {
            const parentProf = prev.profiles.find(
              p => p.profile_type === 'PARENT' && p.id !== profileId
            );
            newActiveProfileId =
              parentProf?.id ||
              prev.profiles.find(p => p.id !== profileId)?.id ||
              null;

            if (newActiveProfileId) {
              localStorage.setItem('active_profile_id', newActiveProfileId);
            } else {
              localStorage.removeItem('active_profile_id');
            }
          }

          return {
            ...prev,
            activeProfileId: newActiveProfileId,
            isLoading: false,
          };
        });
      } catch (error) {
        console.error('❌ Erreur suppression:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : t('profiles-children:errors.deleteError', 'Erreur suppression'),
        }));
        throw error;
      }
    },
    [api, t, loadProfiles]
  );

  // ✅ UPDATE PROFILE AVEC AUTO-REFRESH
  const updateProfile = useCallback(
    async (profileId: string, updateData: any): Promise<void> => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        await api.updateFamilyProfile(profileId, updateData);

        // ✅ REFRESH AUTO APRÈS UPDATE
        await loadProfiles({ forceRefresh: true });

        setState(prev => ({ ...prev, isLoading: false }));
      } catch (error) {
        console.error('❌ Erreur mise à jour:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : t('profiles-children:errors.updateError', 'Erreur mise à jour'),
        }));
        throw error;
      }
    },
    [api, t, loadProfiles]
  );

  // ✅ TOGGLE PROFILE SELECTION (multi-select pour filtres recommandations)
  const toggleProfileSelection = useCallback((profileId: string) => {
    setState(prev => {
      const isSelected = prev.selectedProfileIds.includes(profileId);
      const next = isSelected
        ? prev.selectedProfileIds.filter(id => id !== profileId)
        : [...prev.selectedProfileIds, profileId];
      localStorage.setItem('glowp_selected_profiles', JSON.stringify(next));
      return { ...prev, selectedProfileIds: next };
    });
  }, []);

  // ✅ SWITCH PROFILE ULTRA SMOOTH
  const switchProfile = useCallback(
    async (profileId: string): Promise<void> => {
      const profile = state.profiles.find(p => p.id === profileId);
      if (!profile) {
        throw new Error(
          t('profiles-children:errors.profileNotFound', 'Profil introuvable')
        );
      }

      setState(prev => ({ ...prev, activeProfileId: profileId }));
      localStorage.setItem('active_profile_id', profileId);
    },
    [state.profiles, t]
  );

  // ✅ REFRESH MANUEL ACCESSIBLE
  const refreshProfiles = useCallback(async () => {
    await loadProfiles({ forceRefresh: true });
  }, [loadProfiles]);

  // ============================================================================
  // 🎯 EFFECTS ULTRA OPTIMISÉS - CORRECTION BOUCLE INFINIE
  // ============================================================================

  // ✅ AUTO-LOAD AU MOUNT - UNE SEULE FOIS
  useEffect(() => {
    if (!isAuthenticated || !user || !getToken()) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    // ✅ CHARGE SEULEMENT SI PAS DÉJÀ CHARGÉ
    if (hasLoadedRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      loadProfiles();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, user]); // ✅ PAS getToken ni loadProfiles

  // ✅ RESTORE ACTIVE PROFILE
  useEffect(() => {
    const savedActiveId = localStorage.getItem('active_profile_id');
    if (savedActiveId && state.profiles.length > 0 && !state.activeProfileId) {
      const profileExists = state.profiles.some(p => p.id === savedActiveId);
      if (profileExists) {
        setState(prev => ({ ...prev, activeProfileId: savedActiveId }));
      }
    }
  }, [state.profiles, state.activeProfileId]);

  // ============================================================================
  // 🎁 RETOUR DU HOOK - ULTRA COMPLET !
  // ============================================================================

  return {
    // État
    profiles: state.profiles,
    activeProfile,
    childProfiles,
    parentProfile,
    isLoading: state.isLoading,
    error: state.error,

    // Actions CRUD
    loadProfiles,
    createProfile,
    deleteProfile,
    updateProfile,
    switchProfile,
    refreshProfiles,
    toggleProfileSelection,
    selectedProfiles,
    selectedProfileIds: state.selectedProfileIds,

    // Helpers
    needsOnboarding: state.needsOnboarding,
    hasProfiles: state.profiles.length > 0,
    hasActiveProfile: !!activeProfile,
    activeProfileName:
      activeProfile?.profile_name || t('common:navigation.profile', 'Profil'),
    activeProfileType: activeProfile?.profile_type || null,

    // ✅ NOUVEAUX HELPERS PREMIUM
    profileCount: state.profiles.length,
    childCount: childProfiles.length,
    lastRefreshTime: state.lastRefreshTime,
  };
}

export default useProfiles;
